import { API_BASE_URL, REQUEST_TIMEOUT } from '../constants';
import type { Post, PostFilters, PostsResponse, AnimalSpecies, PostStatus, PostReviewSettings } from '../types';

class PostService {
  private async fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('admin_token');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
          ...options.headers,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: 'Erro ao processar requisição'
        }));
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Tempo limite da requisição excedido');
        }
        throw error;
      }
      throw new Error('Erro desconhecido');
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async getAllPosts(filters: PostFilters = {}): Promise<PostsResponse> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.speciesId) params.append('speciesId', filters.speciesId.toString());
    if (filters.city) params.append('city', filters.city);
    if (filters.region) params.append('region', filters.region);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.status) params.append('status', filters.status);
    if (filters.reviewStatus) params.append('reviewStatus', filters.reviewStatus);

    const queryString = params.toString();
    const url = `${API_BASE_URL}/admin/posts${queryString ? '?' + queryString : ''}`;

    const response = await this.fetchWithAuth<PostsResponse | Post[]>(url);

    if (Array.isArray(response)) {
      return {
        posts: response,
        data: response,
        total: response.length,
        page: filters.page || 1,
        totalPages: 1,
      };
    }

    return response as PostsResponse;
  }

  async getPostById(id: number): Promise<Post> {
    const url = `${API_BASE_URL}/admin/posts/${id}`;
    return this.fetchWithAuth<Post>(url);
  }

  async updatePostReviewStatus(id: number, reviewStatus: 'APPROVED' | 'REJECTED'): Promise<{ id: number; reviewStatus: 'APPROVED' | 'REJECTED'; status: PostStatus }> {
    const url = `${API_BASE_URL}/admin/posts/${id}/review-status`;
    return this.fetchWithAuth<{ id: number; reviewStatus: 'APPROVED' | 'REJECTED'; status: PostStatus }>(url, {
      method: 'PUT',
      body: JSON.stringify({ reviewStatus }),
    });
  }

  async getPostReviewSettings(): Promise<PostReviewSettings> {
    const url = `${API_BASE_URL}/admin/post-review-settings`;
    return this.fetchWithAuth<PostReviewSettings>(url);
  }

  async updatePostReviewSettings(settings: PostReviewSettings): Promise<PostReviewSettings> {
    const url = `${API_BASE_URL}/admin/post-review-settings`;
    return this.fetchWithAuth<PostReviewSettings>(url, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getAllSpecies(): Promise<AnimalSpecies[]> {
    const url = `${API_BASE_URL}/animal-species`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        throw new Error('Erro ao buscar espécies');
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Tempo limite da requisição excedido');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

export const postService = new PostService();
